import responsiveImages from "./responsiveImages.json";
import { localData } from "../../api/localData";
import React from "react";
import {
  Box,
  Typography,
  Card,
  CircularProgress,
  Fade,
  Container,
  Grid,
  Button,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { ArrowForward, ArrowOutward } from "@mui/icons-material";

const COLORS = {
  primary: "#123465",
  secondary: "#f9a836",
  lightBg: "#f5faff",
  white: "#fff",
};

const lineClamp = (lines = 2) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

const productArticles = localData.articles.filter((item) => item.type === 1);

export default function Produits() {
  const articles = productArticles;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const topProduits = articles.slice(0, 6);

  return (
    <Box
      sx={{
        py: { xs: 7, md: 10 },
        bgcolor: COLORS.lightBg,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          bottom: -100,
          left: -100,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, rgba(18,52,101,0.04) 0%, transparent 70%)`,
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Fade in appear={false} timeout={600}>
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
            {/* Eyebrow */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                bgcolor: `rgba(18,52,101,0.06)`,
                border: `1px solid rgba(18,52,101,0.1)`,
                borderRadius: 999,
                px: 2,
                py: 0.6,
                mb: 2.5,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: COLORS.primary,
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: COLORS.primary,
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                }}
              >
                Catalogue produits
              </Typography>
            </Box>

            <Typography
              variant="h2"
              sx={{
                color: COLORS.primary,
                fontWeight: 800,
                fontSize: { xs: "1.75rem", sm: "2.1rem", md: "2.5rem" },
                lineHeight: 1.2,
                letterSpacing: -0.5,
                mb: 2,
              }}
            >
              Nos produits
            </Typography>

            <Typography
              sx={{
                color: "rgba(15,23,42,0.6)",
                fontSize: { xs: "0.97rem", md: "1.05rem" },
                lineHeight: 1.8,
                maxWidth: 600,
                mx: "auto",
                fontWeight: 400,
              }}
            >
              Terminaux mobiles, imprimantes, scanners, tablettes industrielles,
              infrastructures Wi-Fi et consommables pour la traçabilité.
            </Typography>
          </Box>
        </Fade>

        {/* Loader ou cartes */}
        {articles.length === 0 ? (
          <Box sx={{ display: "flex", height: 220, justifyContent: "center", alignItems: "center" }}>
            <CircularProgress size={60} thickness={4} sx={{ color: COLORS.secondary }} />
          </Box>
        ) : (
          <>
            <Fade in appear={false} timeout={700}>
              <Grid container spacing={{ xs: 2, md: 2.5 }}>
                {topProduits.map((item, idx) => {
                  const title = item.shortTitre || item.titre || "Produit";
                  const desc =
                    item.shortDescription ||
                    item.resume ||
                    item.description ||
                    "Découvrez les détails du produit.";
                  const image = item.shortImage || item.image;
                  const link = item.slug || "#";
                  const isNew = item?.isNew ?? item?.nouveau ?? false;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={item.idRefArticle || item.id || title}>
                      <Card
                        component={NavLink}
                        to={link}
                        onClick={() => window.scrollTo(0, 0)}
                        sx={{
                          textDecoration: "none",
                          borderRadius: "18px",
                          overflow: "hidden",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid rgba(15,23,42,0.07)",
                          boxShadow: "0 2px 16px rgba(15,23,42,0.05)",
                          transition: "transform .25s ease, box-shadow .25s ease",
                          cursor: "pointer",
                          bgcolor: COLORS.white,
                          "&:hover": {
                            transform: "translateY(-6px)",
                            boxShadow: "0 20px 48px rgba(15,23,42,0.12)",
                          },
                          "&:hover .prod-img": {
                            transform: "scale(1.08)",
                          },
                          "&:hover .prod-overlay": {
                            opacity: 1,
                          },
                        }}
                      >
                        {/* Image zone */}
                        <Box
                          sx={{
                            position: "relative",
                            aspectRatio: "16 / 9",
                            overflow: "hidden",
                            bgcolor: "rgba(15,23,42,0.03)",
                          }}
                        >
                          <Box
                            className="prod-img"
                            component="img"
                            src={image}
                      srcSet={responsiveImages[image]}
                      sizes="(max-width: 599px) calc(100vw - 32px), (max-width: 899px) calc(50vw - 32px), 380px"
                      decoding="async"
                            alt={title}
                            loading="lazy"
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              transition: "transform 500ms ease",
                            }}
                          />

                          {/* Hover overlay */}
                          <Box
                            className="prod-overlay"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              bgcolor: "rgba(18,52,101,0.55)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: 0,
                              transition: "opacity .25s ease",
                            }}
                          >
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 2.25,
                                py: 1,
                                borderRadius: "10px",
                                fontWeight: 700,
                                fontSize: "0.88rem",
                                bgcolor: COLORS.secondary,
                                color: COLORS.primary,
                                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                              }}
                            >
                              Voir les détails <ArrowOutward sx={{ fontSize: "16px" }} />
                            </Box>
                          </Box>

                          {/* Badge Nouveau */}
                          {isNew && (
                            <Chip
                              label="Nouveau"
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 12,
                                left: 12,
                                bgcolor: COLORS.secondary,
                                color: COLORS.primary,
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                borderRadius: "6px",
                                height: 22,
                              }}
                            />
                          )}
                        </Box>

                        {/* Content */}
                        <Box
                          sx={{
                            px: 2.5,
                            pt: 2,
                            pb: 2.5,
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "1rem",
                              fontWeight: 700,
                              color: COLORS.primary,
                              lineHeight: 1.35,
                              mb: 0.75,
                              ...lineClamp(2),
                            }}
                          >
                            {title}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: "rgba(55,65,81,0.75)",
                              lineHeight: 1.65,
                              ...lineClamp(2),
                            }}
                          >
                            {desc}
                          </Typography>

                          {/* Bottom link */}
                          <Box
                            sx={{
                              mt: "auto",
                              pt: 1.75,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: COLORS.secondary,
                              fontSize: "0.82rem",
                              fontWeight: 700,
                            }}
                          >
                            En savoir plus
                            <ArrowForward sx={{ fontSize: "14px" }} />
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Fade>

            {/* CTA */}
            <Fade in appear={false} timeout={800}>
              <Box
                sx={{
                  mt: { xs: 5, md: 6 },
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 760,
                    px: { xs: 2.3, md: 3 },
                    py: { xs: 2.2, md: 2.4 },
                    borderRadius: "16px",
                    bgcolor: COLORS.white,
                    border: "1px solid rgba(18,52,101,0.09)",
                    boxShadow: "0 16px 38px rgba(18,52,101,0.08)",
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: COLORS.primary,
                        fontWeight: 800,
                        fontSize: { xs: "1rem", md: "1.08rem" },
                        mb: 0.5,
                      }}
                    >
                      Besoin d'un conseil produit ?
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(55,65,81,0.72)",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                      }}
                    >
                      Dites-nous votre usage terrain, nous vous orientons vers le bon equipement.
                    </Typography>
                  </Box>

                  <Button
                  component={NavLink}
                  to="/contactus"
                  onClick={() => window.scrollTo(0, 0)}
                  variant="contained"
                  endIcon={<ArrowForward />}
                  sx={{
                    borderRadius: "12px",
                    px: 2.6,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    bgcolor: COLORS.primary,
                    color: COLORS.white,
                    boxShadow: "none",
                    whiteSpace: isMobile ? "normal" : "nowrap",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: COLORS.secondary,
                      color: COLORS.primary,
                      transform: "translateY(-1px)",
                      boxShadow: "0 10px 24px rgba(249,168,54,0.26)",
                    },
                  }}
                >
                    Parler a un expert
                  </Button>
                </Box>
              </Box>
            </Fade>
          </>
        )}
      </Container>
    </Box>
  );
}
